import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import AdminOverview from './admin/AdminOverview';
import AdminPeople from './admin/AdminPeople';
import AdminTeams from './admin/AdminTeams';
import AdminSchedule from './admin/AdminSchedule';
import AdminMeals from './admin/AdminMeals';
import AdminAccess from './admin/AdminAccess';
import AdminManual from './admin/AdminManual';
import AdminPast from './admin/AdminPast';
import AdminSettings from './admin/AdminSettings';
import TeamLeadView from './teamlead/TeamLeadView';
import KidsOverview from './kids/KidsOverview';
import KidsSmallGroups from './kids/KidsSmallGroups';
import KidsAllergies from './kids/KidsAllergies';
import KidsDailyDetails from './kids/KidsDailyDetails';
import KidsDetailSheets from './kids/KidsDetailSheets';
import KidsContributing from './kids/KidsContributing';
import KidsRotationsTab from './kids/KidsRotationsTab';
import KidsSettings from './kids/KidsSettings';
import PersonPanel from './shared/PersonPanel';
import TeamPanel from './shared/TeamPanel';
import EditTeamModal from './modals/EditTeamModal';
import Toast from './shared/Toast';
import { getTeams, upsertTeam, deleteTeam, getTeamRoles, getTeamData, getSettings } from '../utils/api';
import { isKidsTeam, kidsThemeClass } from '../theme/kidsTheme';
import styles from './AppShell.module.css';

// ── URL ↔ view-ID helpers ──────────────────────────────────
const ADMIN_VIEW_TO_SLUG = {
  overview: 'overview', people: 'people', teams: 'teams',
  schedule: 'schedule', meals: 'meals', access: 'access',
  'admin-manual': 'manual', 'past-2025': 'past-2025', settings: 'settings',
};
const SLUG_TO_ADMIN_VIEW = Object.fromEntries(
  Object.entries(ADMIN_VIEW_TO_SLUG).map(([v, s]) => [s, v])
);

// Kids staff view sub-tabs (combined admin + team-lead view for the Kids team).
// Longest-match-first so "detail-sheets" wins over anything shorter.
const KIDS_SUBS = ['detail-sheets', 'contributing', 'rotations', 'allergies', 'overview', 'settings', 'groups', 'daily'];

function viewToPath(viewId) {
  if (ADMIN_VIEW_TO_SLUG[viewId]) return `/admin/${ADMIN_VIEW_TO_SLUG[viewId]}`;
  if (viewId === 'tl-empty') return '/tl';
  if (viewId.startsWith('tl-')) {
    const rest = viewId.slice(3);
    for (const sub of ['people', 'schedule', 'manual']) {
      if (rest.endsWith('-' + sub)) {
        return `/tl/${encodeURIComponent(rest.slice(0, -(sub.length + 1)))}/${sub}`;
      }
    }
    const ri = rest.indexOf('-roster');
    if (ri > 0) {
      // sub is "roster" or "roster-{roleName}" — encode the whole segment so
      // role names with spaces/special chars survive the URL round-trip.
      // Checked BEFORE kids subs so a role name ending in a kids-sub token
      // (e.g. a role literally named "groups") can't hijack the roster view.
      return `/tl/${encodeURIComponent(rest.slice(0, ri))}/${encodeURIComponent(rest.slice(ri + 1))}`;
    }
    for (const sub of KIDS_SUBS) {
      if (rest.endsWith('-' + sub)) {
        return `/tl/${encodeURIComponent(rest.slice(0, -(sub.length + 1)))}/${sub}`;
      }
    }
  }
  return '/admin/overview';
}

function pathToView(pathname) {
  const am = pathname.match(/^\/admin(?:\/(.*))?$/);
  if (am) return SLUG_TO_ADMIN_VIEW[am[1] || 'overview'] || null;
  const tm = pathname.match(/^\/tl(?:\/([^/]+)(?:\/(.+))?)?$/);
  if (tm) {
    if (!tm[1]) return 'tl-empty';
    // Decode BOTH segments — the sub-path carries role names that may contain
    // spaces (e.g. "roster-LCO Tracks" arrives as "roster-LCO%20Tracks").
    const sub = tm[2] ? decodeURIComponent(tm[2]) : 'people';
    return `tl-${decodeURIComponent(tm[1])}-${sub}`;
  }
  return null;
}

function extractTeamIdFromView(viewId) {
  if (!viewId || !viewId.startsWith('tl-')) return null;
  const rest = viewId.slice(3);
  for (const sub of ['people', 'schedule', 'manual']) {
    if (rest.endsWith('-' + sub)) return rest.slice(0, -(sub.length + 1));
  }
  // Roster views resolve before kids subs (see viewToPath).
  const ri = rest.indexOf('-roster');
  if (ri > 0) return rest.slice(0, ri);
  for (const sub of KIDS_SUBS) {
    if (rest.endsWith('-' + sub)) return rest.slice(0, -(sub.length + 1));
  }
  return null;
}

// Which kids sub-tab (if any) the current view points at, e.g. "tl-{id}-groups" → "groups".
// Roster views are never kids subs, even if a role name ends in a kids-sub token.
function kidsSubFromView(viewId) {
  if (!viewId || !viewId.startsWith('tl-')) return null;
  const rest = viewId.slice(3);
  if (rest.indexOf('-roster') > 0) return null;
  for (const sub of KIDS_SUBS) {
    if (rest.endsWith('-' + sub)) return sub;
  }
  return null;
}

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, teamsReady, refreshUser } = useAuth();

  // URL is the source of truth for which view is active
  const currentView = useMemo(
    () => pathToView(location.pathname) || 'overview',
    [location.pathname]
  );
  const setCurrentView = useCallback(
    (viewId) => navigate(viewToPath(viewId)),
    [navigate]
  );
  // Derived from the URL — no separate state needed
  const tlViewActive = currentView.startsWith('tl-') || currentView === 'tl-empty';
  const [selectedTeam, setSelectedTeam] = useState(null);
  // previewTeam is set when admin clicks "View as Team Lead" on a specific team
  const [previewTeam, setPreviewTeam] = useState(null);
  const [prePreviewView, setPrePreviewView] = useState('teams');

  // All teams loaded from spreadsheet
  const [allTeams, setAllTeams] = useState([]);

  // Panel state
  const [openPanel, setOpenPanel] = useState(null);
  const [panelData, setPanelData] = useState(null);

  // Modal state
  const [openModal, setOpenModal] = useState(null);
  const [modalData, setModalData] = useState(null);

  // Edit team modal (opened from TeamPanel or AdminTeams)
  const [editTeamOpen, setEditTeamOpen] = useState(false);
  const [editTeamData, setEditTeamData] = useState(null);

  // Team roles map: { [teamId]: { enabled: bool, roles: string[] } }
  const [teamRolesMap, setTeamRolesMap] = useState({});

  // Script Viewer URL from Settings
  const [scriptViewerUrl, setScriptViewerUrl] = useState('');

  // Toast
  const [toastMsg, setToastMsg] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const showingAdminView = isAdmin && !tlViewActive;

  // Theme by context: teal whenever the active area is a Kids-department team
  // (a both-server sees teal in Kids and the dark theme in their main teams).
  const themeKids = !showingAdminView && isKidsTeam(selectedTeam);

  // Close sidebar on mobile when view changes
  useEffect(() => { setSidebarOpen(false); }, [currentView]);

  // Persist selected team across refreshes (URL handles view & TL state)
  useEffect(() => {
    if (selectedTeam?.id) sessionStorage.setItem('tbco_selected_team_id', selectedTeam.id);
  }, [selectedTeam]);

  // Load all teams from spreadsheet on mount
  useEffect(() => {
    getTeams()
      .then(data => setAllTeams(data || []))
      .catch(err => console.error('Failed to load teams:', err));
  }, []);

  // Load settings for global config (scriptViewerUrl)
  useEffect(() => {
    getSettings()
      .then(s => { if (s?.scriptViewerUrl) setScriptViewerUrl(s.scriptViewerUrl); })
      .catch(() => {});
  }, []);

  // Sync selectedTeam from URL (back/forward, direct link) or sessionStorage
  useEffect(() => {
    if (!allTeams.length) return;
    const teamId = extractTeamIdFromView(currentView);
    if (teamId) {
      if (!selectedTeam || selectedTeam.id !== teamId) {
        const team = allTeams.find(t => t.id === teamId);
        if (team) setSelectedTeam(team);
      }
      return;
    }
    if (!selectedTeam) {
      const storedId = sessionStorage.getItem('tbco_selected_team_id');
      if (storedId) {
        const team = allTeams.find(t => t.id === storedId);
        if (team) setSelectedTeam(team);
      }
    }
  }, [allTeams, currentView, selectedTeam]);

  // Resolve user's team names → team objects.
  // If a name isn't in allTeams (e.g. team has no Brushfire data yet), use { id: name, name } as fallback
  // so it still appears in the sidebar.
  const tlTeams = useMemo(() => {
    if (!user?.teams?.length) return [];
    return user.teams
      .map(name => {
        const found = allTeams.find(t => t.name.toLowerCase() === name.toLowerCase());
        return found || { id: name, name };
      })
      .filter(t => t.name);
  }, [user?.teams, allTeams]);

  // Load roles for all TL teams so Sidebar can render dynamic items.
  // getTeamRoles is now cached so repeated calls are free.
  useEffect(() => {
    const teams = tlTeams.length ? tlTeams : [];
    if (!teams.length) return;
    Promise.all(
      teams.map(t =>
        getTeamRoles(t.id)
          .then(d => ({ id: t.id, enabled: d?.enabled || false, roles: d?.roles || [] }))
          .catch(() => ({ id: t.id, enabled: false, roles: [] }))
      )
    ).then(results => {
      const map = {};
      results.forEach(r => { map[r.id] = { enabled: r.enabled, roles: r.roles }; });
      setTeamRolesMap(map);
    });

    // Prefetch team data in the background so the first click into team lead view is instant
    teams.forEach(t => { getTeamData(t.id).catch(() => {}); });
  }, [tlTeams]);

  const showToast = useCallback((msg) => { setToastMsg(msg); }, []);
  const openPersonPanel = useCallback((person) => { setPanelData(person); setOpenPanel('person'); }, []);
  const openTeamPanel = useCallback((team) => { setPanelData(team); setOpenPanel('team'); }, []);
  const closePanel = useCallback(() => { setOpenPanel(null); setPanelData(null); }, []);

  const handleOpenEditTeam = useCallback((team) => {
    setEditTeamData(team);
    setEditTeamOpen(true);
  }, []);

  async function handleEditTeamSave(team) {
    await upsertTeam(team);
    showToast('Team saved.');
    // Refresh allTeams so panels and sidebar reflect new data
    const fresh = await getTeams().catch(() => allTeams);
    if (fresh) setAllTeams(fresh);
  }

  async function handleEditTeamDelete(id) {
    await deleteTeam(id);
    showToast('Team deleted.');
    const fresh = await getTeams().catch(() => allTeams);
    if (fresh) setAllTeams(fresh);
  }

  // Teams visible in TL view.
  // Preview mode (from "View as Team Lead" button) shows exactly one team.
  // Otherwise use the admin's configured teams (no fallback — empty = no teams assigned).
  const activeTlTeams = previewTeam
    ? [previewTeam]
    : isAdmin
      ? tlTeams
      : tlTeams;

  async function handleToggleTLView() {
    if (!tlViewActive) {
      // Fetch both the user's current team assignments AND the full teams list
      // simultaneously so we always resolve against live spreadsheet data.
      const [freshUser, freshAllTeams] = await Promise.all([
        refreshUser(),
        getTeams().catch(() => allTeams),
      ]);

      // Keep allTeams state in sync
      if (freshAllTeams?.length) setAllTeams(freshAllTeams);

      const teamsList = freshAllTeams?.length ? freshAllTeams : allTeams;
      const freshTeams = (freshUser?.teams || [])
        .map(name => {
          const found = teamsList.find(t => t.name.toLowerCase() === name.toLowerCase());
          return found || { id: name, name };
        })
        .filter(t => t.name);

      const first = freshTeams[0];
      if (first) {
        setSelectedTeam(first);
        setCurrentView(`tl-${first.id}-people`);
      } else {
        setSelectedTeam(null);
        setCurrentView('tl-empty');
      }
    } else {
      const returnView = previewTeam ? prePreviewView : 'overview';
      setPreviewTeam(null);
      setCurrentView(returnView);
      setSelectedTeam(null);
    }
  }

  const handleRolesSaved = useCallback((teamId, enabled, roles) => {
    setTeamRolesMap(prev => ({ ...prev, [teamId]: { enabled, roles } }));
  }, []);

  function viewAsTeamLead(team) {
    setPrePreviewView(currentView);
    setPreviewTeam(team);
    setSelectedTeam(team);
    // Kids-department teams land on the Kids Overview (their staff home);
    // everyone else starts on People as before.
    setCurrentView(isKidsTeam(team) ? `tl-${team.id}-overview` : `tl-${team.id}-people`);
    // Fetch fresh roles so sidebar shows correct tabs immediately
    getTeamRoles(team.id)
      .then(d => handleRolesSaved(team.id, d?.enabled || false, d?.roles || []))
      .catch(() => {});
  }

  // Redirect unknown/root URLs to the appropriate default view.
  // Kids-only users (Mal, her admins, 2ICs, program leads) land on the Kids
  // staff view instead of the main admin dashboard — that IS their app. A
  // kids-only ADMIN can still reach /admin/* by navigating there explicitly.
  const kidsOnlyUser = tlTeams.length > 0 && tlTeams.every(t => isKidsTeam(t));
  useEffect(() => {
    const view = pathToView(location.pathname);
    if (isAdmin && kidsOnlyUser) {
      if (!view) {
        const first = tlTeams[0];
        setSelectedTeam(first);
        navigate(viewToPath(`tl-${first.id}-overview`), { replace: true });
      }
    } else if (isAdmin) {
      if (!view) navigate('/admin/overview', { replace: true });
    } else if (tlTeams.length > 0) {
      if (!view || !view.startsWith('tl-')) {
        const first = tlTeams[0];
        setSelectedTeam(first);
        const home = isKidsTeam(first) ? `tl-${first.id}-overview` : `tl-${first.id}-people`;
        navigate(viewToPath(home), { replace: true });
      }
    }
  }, [isAdmin, kidsOnlyUser, tlTeams, location.pathname, navigate]);

  // Clear preview state when navigating back to admin view
  useEffect(() => {
    if (!tlViewActive && previewTeam) {
      setPreviewTeam(null);
    }
  }, [tlViewActive, previewTeam]);

  function renderView() {
    if (!showingAdminView) {
      // Kids staff view: kids-specific tabs render their own components; the
      // shared tabs (People / rosters / schedule / docs) stay on TeamLeadView.
      const kidsSub = kidsSubFromView(currentView);
      if (kidsSub && isKidsTeam(selectedTeam)) {
        const common = { team: selectedTeam, showToast, setCurrentView };
        switch (kidsSub) {
          case 'overview':      return <KidsOverview {...common} />;
          case 'groups':        return <KidsSmallGroups {...common} />;
          case 'allergies':     return <KidsAllergies {...common} />;
          case 'daily':         return <KidsDailyDetails {...common} />;
          case 'detail-sheets': return <KidsDetailSheets {...common} />;
          case 'contributing':  return <KidsContributing {...common} />;
          case 'rotations':     return <KidsRotationsTab {...common} />;
          case 'settings':      return <KidsSettings {...common} />;
          default: break;
        }
      }
      if (activeTlTeams.length === 0) {
        return (
          <div style={{ padding: '80px 40px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 18, fontWeight: 800, color: 'var(--black)', marginBottom: 12 }}>
              No teams assigned
            </div>
            <div style={{ maxWidth: 380, margin: '0 auto' }}>
              Go to <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>Team Lead Access</strong> and add teams to your profile, or use the <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>Teams</strong> tab and click <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>View as Team Lead</strong> to preview a specific team.
            </div>
          </div>
        );
      }
      return (
        <TeamLeadView
          teams={activeTlTeams}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          currentView={currentView}
          setCurrentView={setCurrentView}
          openPersonPanel={openPersonPanel}
          showToast={showToast}
          setOpenModal={setOpenModal}
          setModalData={setModalData}
          onRolesLoaded={handleRolesSaved}
          urlTeamId={extractTeamIdFromView(currentView)}
        />
      );
    }

    switch (currentView) {
      case 'overview':
        return <AdminOverview setCurrentView={setCurrentView} setOpenModal={setOpenModal} showToast={showToast} />;
      case 'people':
        return <AdminPeople openPersonPanel={openPersonPanel} setOpenModal={setOpenModal} showToast={showToast} />;
      case 'teams':
        return <AdminTeams openTeamPanel={openTeamPanel} onViewAsTeamLead={viewAsTeamLead} onRolesSaved={handleRolesSaved} showToast={showToast} />;
      case 'schedule':
        return <AdminSchedule setOpenModal={setOpenModal} showToast={showToast} />;
      case 'meals':
        return <AdminMeals />;
      case 'access':
        return <AdminAccess setOpenModal={setOpenModal} showToast={showToast} />;
      case 'admin-manual':
        return <AdminManual showToast={showToast} />;
      case 'past-2025':
        return <AdminPast />;
      case 'settings':
        return <AdminSettings showToast={showToast} />;
      default:
        return <AdminOverview setCurrentView={setCurrentView} setOpenModal={setOpenModal} showToast={showToast} />;
    }
  }

  return (
    <div className={`${styles.shell}${kidsThemeClass(themeKids)}`}>
      <Sidebar
        currentView={currentView}
        setCurrentView={setCurrentView}
        isAdmin={isAdmin}
        showingAdminView={showingAdminView}
        tlViewActive={tlViewActive}
        selectedTeam={selectedTeam}
        setSelectedTeam={setSelectedTeam}
        teams={showingAdminView ? [] : (isAdmin ? activeTlTeams : tlTeams)}
        teamRolesMap={teamRolesMap}
        mobileOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        scriptViewerUrl={scriptViewerUrl}
      />
      {sidebarOpen && (
        <div className="sidebar-overlay open" onClick={() => setSidebarOpen(false)} />
      )}
      <div className={styles.main}>
        <Topbar
          currentView={currentView}
          isAdmin={isAdmin}
          tlViewActive={tlViewActive}
          onToggleTLView={handleToggleTLView}
          previewTeamName={previewTeam?.name}
          teamsReady={teamsReady && allTeams.length > 0}
          showToast={showToast}
          onOpenSidebar={() => setSidebarOpen(true)}
        />
        <div className={styles.content}>
          {renderView()}
        </div>
      </div>

      <PersonPanel open={openPanel === 'person'} onClose={closePanel} person={panelData} showToast={showToast} onPersonUpdated={setPanelData} />
      <TeamPanel
        open={openPanel === 'team'}
        onClose={closePanel}
        team={panelData}
        showToast={showToast}
        setCurrentView={setCurrentView}
        onViewAsTeamLead={viewAsTeamLead}
        onEdit={handleOpenEditTeam}
      />

      <EditTeamModal
        open={editTeamOpen}
        onClose={() => { setEditTeamOpen(false); setEditTeamData(null); }}
        team={editTeamData}
        onSave={handleEditTeamSave}
        onDelete={handleEditTeamDelete}
        onRolesSaved={handleRolesSaved}
        showToast={showToast}
      />

      <Toast message={toastMsg} onDone={() => setToastMsg('')} />
    </div>
  );
}
